// Phase II · Wave D.1 · 40-locale master list · single source of truth.
// Mirrors backend/constants/locales.py (GLMS_LOCALES tuple). Any change
// here must be reflected there (and vice versa). The order is kept
// stable because the LocaleSwitcher consumes the first 10 entries as
// its "popular" group and the remainder as the "all others" section.
//
// Each entry carries:
//   - code     · BCP-47 slug used in URLs / cookies / API headers
//   - name     · English name shown next to native (debug / fallback)
//   - native   · Native-language label shown in the LocaleSwitcher
//   - popular  · True for the top-10 "popular" bucket (dropdown header)
//   - rtl      · Reading direction hint · `true` for Arabic / Hebrew /
//               (future) Farsi / Urdu
//
// NOTE: Wave D.0 translation pipeline currently accepts only "it".
// Wave D.1 scaffolds the LocaleSwitcher + routing for all 40 so the
// framework is ready when D.2 → D.5 populate real translations.
export const GLMS_LOCALES = [
    // Top-10 popular
    { code: "en-US",  name: "English (US)",          native: "English",                 popular: true,  rtl: false },
    { code: "zh-CN",  name: "Chinese (Simplified)",  native: "中文",                     popular: true,  rtl: false },
    { code: "es",     name: "Spanish",                native: "Español",                 popular: true,  rtl: false },
    { code: "hi",     name: "Hindi",                  native: "हिन्दी",                   popular: true,  rtl: false },
    { code: "ar",     name: "Arabic",                 native: "العربية",                 popular: true,  rtl: true  },
    { code: "pt-BR",  name: "Portuguese (Brazil)",   native: "Português",                popular: true,  rtl: false },
    { code: "ru",     name: "Russian",                native: "Русский",                 popular: true,  rtl: false },
    { code: "ja",     name: "Japanese",               native: "日本語",                   popular: true,  rtl: false },
    { code: "de",     name: "German",                 native: "Deutsch",                 popular: true,  rtl: false },
    { code: "fr",     name: "French",                 native: "Français",                popular: true,  rtl: false },
    // Rest (alphabetical)
    { code: "af",     name: "Afrikaans",              native: "Afrikaans",               popular: false, rtl: false },
    { code: "bg",     name: "Bulgarian",              native: "Български",               popular: false, rtl: false },
    { code: "ca",     name: "Catalan",                native: "Català",                  popular: false, rtl: false },
    { code: "cs",     name: "Czech",                  native: "Čeština",                 popular: false, rtl: false },
    { code: "da",     name: "Danish",                 native: "Dansk",                   popular: false, rtl: false },
    { code: "el",     name: "Greek",                  native: "Ελληνικά",                popular: false, rtl: false },
    { code: "et",     name: "Estonian",               native: "Eesti",                   popular: false, rtl: false },
    { code: "eu",     name: "Basque",                 native: "Euskara",                 popular: false, rtl: false },
    { code: "fi",     name: "Finnish",                native: "Suomi",                   popular: false, rtl: false },
    { code: "fil",    name: "Filipino",               native: "Filipino",                popular: false, rtl: false },
    { code: "he",     name: "Hebrew",                 native: "עברית",                   popular: false, rtl: true  },
    { code: "hr",     name: "Croatian",               native: "Hrvatski",                popular: false, rtl: false },
    { code: "hu",     name: "Hungarian",              native: "Magyar",                  popular: false, rtl: false },
    { code: "id",     name: "Indonesian",             native: "Bahasa Indonesia",        popular: false, rtl: false },
    { code: "it",     name: "Italian",                native: "Italiano",                popular: false, rtl: false },
    { code: "ko",     name: "Korean",                 native: "한국어",                   popular: false, rtl: false },
    { code: "lt",     name: "Lithuanian",             native: "Lietuvių",                popular: false, rtl: false },
    { code: "lv",     name: "Latvian",                native: "Latviešu",                popular: false, rtl: false },
    { code: "ms",     name: "Malay",                  native: "Bahasa Melayu",           popular: false, rtl: false },
    { code: "nl",     name: "Dutch",                  native: "Nederlands",              popular: false, rtl: false },
    { code: "no",     name: "Norwegian",              native: "Norsk",                   popular: false, rtl: false },
    { code: "pl",     name: "Polish",                 native: "Polski",                  popular: false, rtl: false },
    { code: "ro",     name: "Romanian",               native: "Română",                  popular: false, rtl: false },
    { code: "sk",     name: "Slovak",                 native: "Slovenčina",              popular: false, rtl: false },
    { code: "sl",     name: "Slovenian",              native: "Slovenščina",             popular: false, rtl: false },
    { code: "sv",     name: "Swedish",                native: "Svenska",                 popular: false, rtl: false },
    { code: "th",     name: "Thai",                   native: "ไทย",                     popular: false, rtl: false },
    { code: "tr",     name: "Turkish",                native: "Türkçe",                  popular: false, rtl: false },
    { code: "uk",     name: "Ukrainian",              native: "Українська",              popular: false, rtl: false },
    { code: "vi",     name: "Vietnamese",             native: "Tiếng Việt",              popular: false, rtl: false },
];

export const GLMS_LOCALE_CODES = GLMS_LOCALES.map((l) => l.code);
export const GLMS_LOCALE_SET   = new Set(GLMS_LOCALE_CODES);
export const GLMS_POPULAR      = GLMS_LOCALES.filter((l) => l.popular);
export const GLMS_REST         = GLMS_LOCALES.filter((l) => !l.popular);

export const DEFAULT_LOCALE    = "en-US";
export const LOCALE_COOKIE     = "glms_locale";
export const LOCALE_COOKIE_TTL = 365; // days · 1 yr

// Legacy alias — "en" entered the codebase (M14 I18nContext) before
// Wave D.1 normalised on BCP-47 slugs. Keep the alias so URLs typed
// as /en/... still resolve.
export const LOCALE_ALIASES = { en: "en-US" };

export function normaliseLocale(raw) {
    if (!raw || typeof raw !== "string") return DEFAULT_LOCALE;
    const v = raw.trim();
    if (!v) return DEFAULT_LOCALE;
    if (GLMS_LOCALE_SET.has(v)) return v;
    if (LOCALE_ALIASES[v]) return LOCALE_ALIASES[v];
    // Case-tolerant match (es: "DE" → "de").
    const lower = v.toLowerCase();
    for (const code of GLMS_LOCALE_CODES) {
        if (code.toLowerCase() === lower) return code;
    }
    if (LOCALE_ALIASES[lower]) return LOCALE_ALIASES[lower];
    return DEFAULT_LOCALE;
}

export function isValidLocale(raw) {
    if (!raw || typeof raw !== "string") return false;
    const v = raw.trim();
    if (GLMS_LOCALE_SET.has(v) || LOCALE_ALIASES[v]) return true;
    const lower = v.toLowerCase();
    for (const code of GLMS_LOCALE_CODES) {
        if (code.toLowerCase() === lower) return true;
    }
    return !!LOCALE_ALIASES[lower];
}

// Paths that must NEVER be locale-prefixed (static assets, backend
// API, OAuth callbacks, authenticated app shells). The
// LocaleRouteWrapper short-circuits on these so the user keeps a
// bare URL.
//
// Phase VI.5 · Wave C · Hotfix R1 · aggiunti: /sa, /admin, /v2,
// /dashboard, /products, /signup, /login, /signout, /_next, /images.
// Prima della R1, basename.js interpretava `/sa` come candidate
// locale (Sanskrit) e lo riscriveva a `/en-US/*` · idem per
// qualsiasi 2-letter prefisso non valido.
export const SKIP_PATHS = [
    // Backend / static / service worker
    "/api/",
    "/static/",
    "/_next/",
    "/images/",
    "/favicon.ico",
    "/robots.txt",
    "/sitemap.xml",
    "/manifest.json",
    "/service-worker.js",
    // OAuth / auth system paths
    "/auth/callback",
    "/auth/reset-password",
    // Wave C R1 · authenticated app shells (nessun locale wrap)
    "/sa/",
    "/sa",
    "/admin/",
    "/admin",
    "/v2/",
    "/v2",
    // Wave RF.1 · nuovo canonical user shell (sostituisce /v2/*)
    "/app/",
    "/app",
    "/dashboard",
    "/products",
    "/signup",
    "/login",
    "/signout",
];

export function isSkipPath(path) {
    if (!path || typeof path !== "string") return false;
    return SKIP_PATHS.some((p) => {
        if (path === p) return true;
        // Match prefix only when boundary is a slash or end-of-string.
        // Previene falsi positivi tipo `/dashboards` → `/dashboard`.
        if (p.endsWith("/")) return path.startsWith(p);
        return path.startsWith(p + "/") || path === p;
    });
}
